import {
  BaseRepository,
  IBaseModel,
  IListFetchParams,
  MN_RESOURCE_SKILLS,
  MN_RESOURCES,
  MN_SKILLS,
  MODULE,
} from '../common';
import type { Prisma } from '@prisma/client';
import * as z from 'zod';
import type { KeyValue } from '@jobcost-monorepo/utils';

type ResourceSkill = Prisma.$ResourceSkillPayload['scalars'];

export type IResourceSkill = ResourceSkill &
  Omit<IBaseModel, 'createdAt' | 'updatedAt' | 'id'> & {
    resource: KeyValue;
    skill: KeyValue;
  };

export interface IResourceSkillFetchParams extends IListFetchParams {
  resourceId?: number;
  skillId?: number;
  level?: number;
}

class ResourceSkillRepository extends BaseRepository<
  IResourceSkill,
  IResourceSkillFetchParams
> {
  constructor(moduleName: MODULE) {
    super(moduleName);
    this.keyValueMapFields = {
      idlinkedField: 'id',
      descriptionlinkedField: ['resource.name', 'skill.name'],
    };
    this.gridFieldsOptions = [
      {
        visible: true,
        position: 0,
        linkedField: 'id',
        type: '',
        align: 'left',
        width: '12rem',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 1,
        linkedField: 'resource.name',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 2,
        linkedField: 'skill.name',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 3,
        linkedField: 'level',
        type: '',
        align: 'right',
        width: 'w-full',
        sortable: false,
        formatType: 'number',
      },
    ];

    this.gridFiltersOptions = [
      {
        position: { x: 0, y: 0 },
        linkedField: 'resourceId',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_RESOURCES,
        translationModuleName: MN_RESOURCES,
      },
      {
        position: { x: 0, y: 1 },
        linkedField: 'skillId',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_SKILLS,
        translationModuleName: MN_SKILLS,
      },
    ];

    this.formFieldsOptions = [
      {
        visible: false,
        readOnly: true,
        position: { x: 0, y: 0 },
        linkedField: 'id',
        type: 'string',
        width: '12rem',
        formatType: 'input',
        moduleName: MN_RESOURCE_SKILLS,
        translationModuleName: MN_RESOURCE_SKILLS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 0 },
        linkedField: 'resourceId',
        linkedFieldForDefaultValue: 'resource.name',
        type: 'string',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_RESOURCES,
        translationModuleName: MN_RESOURCE_SKILLS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 0 },
        linkedField: 'skillId',
        linkedFieldForDefaultValue: 'skill.name',
        type: 'string',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_SKILLS,
        translationModuleName: MN_RESOURCE_SKILLS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 2, y: 0 },
        linkedField: 'level',
        type: 'number',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_RESOURCE_SKILLS,
        translationModuleName: MN_RESOURCE_SKILLS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 1 },
        linkedField: 'note',
        type: 'number',
        width: 'w-full',
        formatType: 'textArea',
        moduleName: MN_RESOURCE_SKILLS,
        translationModuleName: MN_RESOURCE_SKILLS,
      },
    ];

    // @ts-ignore
    this.formValidationSchema = z.object({
      id: z.number().nullable().optional(),
      resourceId: z.number({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      skillId: z.number({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      level: z
        .number({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .nonnegative({ message: 'nonNegativeNumber' })
        .min(0, { message: 'min_num_0' })
        .max(10, { message: 'max_num_10' }),
      note: z
        .string()
        .min(2, { message: 'min_2' })
        .max(255, { message: 'max_255' })
        .nullable()
        .optional(),
    });
  }
}

export default ResourceSkillRepository;
